import { useState } from "react";
import { Button, Drawer, Popconfirm, Tooltip, message, Tag } from "antd";
import { Modal } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ReconciliationOutlined,
} from "@ant-design/icons";
import NotebooksImg from "../../../assets/notebooks.svg";
import { ManualTriggersTable } from "./ManualTriggers.styled";
import ManualEmailTriggerForm from "./ManualTriggerForm";
import { useAppDispatch } from "../../../redux/hooks";
import { deleteManualEmailTrigger } from "../../../redux/emails/emailsActions";
import { getEmailDeliveryReportTrigger } from "../../../redux/emails/apiService";
import EmailDeliveryReport from "../../../components/EmailDeliveryReport";

function ManualTriggers({
  data,
  surveyEnumerators,
  emailConfigData,
  fetchManualTriggers,
}: any) {
  const [paginationPageSize, setPaginationPageSize] = useState<number>(25);
  const [isEditManualDrawerVisible, setIsEditManualDrawerVisible] =
    useState(false);
  const [editTriggerValues, setEditTriggerValues] = useState();
  const dispatch = useAppDispatch();

  const showEditManualDrawer = () => {
    setIsEditManualDrawerVisible(true);
  };

  const closeEditManualDrawer = () => {
    setIsEditManualDrawerVisible(false);
  };

  const [showModal, setShowModal] = useState<boolean | number>(false);
  const handleClose = () => setShowModal(false);
  const handleShow = (id: any) => setShowModal(id);
  const handleOk = () => setShowModal(false);

  const formatDate = (date: any) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const format12HourTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const adjustedHour = hour % 12 || 12;
    return `${adjustedHour}:${minutes} ${period}`;
  };

  const handleDeleteTrigger = async (trigger: any) => {
    try {
      const result = await dispatch(
        deleteManualEmailTrigger({
          id: trigger.manual_email_trigger_uid,
          email_config_uid: trigger.email_config_uid,
        })
      );

      if (result.payload?.data?.success) {
        message.success("Email manual trigger deleted successfully");
        fetchManualTriggers();
      } else {
        message.error("Failed to delete trigger");
      }
    } catch (error) {
      message.error("An error occurred while deleting trigger");
    }
  };

  const [deliveryReportData, setDeliveryReportData] = useState<any>([]);

  const fetchDeliveryReport = async (
    email_config_uid: string,
    manual_email_trigger_uid: string
  ) => {
    try {
      const result = await getEmailDeliveryReportTrigger(
        email_config_uid,
        manual_email_trigger_uid
      );
      if ((result as any).data?.success) {
        setDeliveryReportData((result as any).data.data);
      } else {
        setDeliveryReportData([]);
      }
    } catch (error) {
      message.error("An error occurred while fetching email delivery report");
    }
  };

  const handleEditTrigger = (trigger: any) => {
    setEditTriggerValues(trigger);
    showEditManualDrawer();
  };
  const manualTriggerColumns = [
    {
      title: "Config Name",
      dataIndex: "config_name",
      sorter: (a: any, b: any) => a.config_name.localeCompare(b.config_name),
    },
    {
      title: "Trigger Date",
      dataIndex: "date",
      render: (text: any, record: any) => {
        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        };
        const date = new Date(record.date);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      },
      sorter: (a: any, b: any) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Trigger Time",
      dataIndex: "time",
      render: (text: any) => format12HourTime(text),
      sorter: (a: any, b: any) => a.time.localeCompare(b.time),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: any, record: any) => {
        let tagColor = "gray";
        if (record.status === "sent") {
          tagColor = "green";
        } else if (record.status === "failed") {
          tagColor = "red";
        }
        return <Tag color={tagColor}>{record.status.toUpperCase()}</Tag>;
      },
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },

    {
      title: "Action",
      key: "action",
      render: (text: any, record: any) => (
        <div>
          <>
            <Button
              type="link"
              icon={<ReconciliationOutlined />}
              style={{ color: "green" }}
              onClick={() => {
                fetchDeliveryReport(
                  record.email_config_uid,
                  record.manual_email_trigger_uid
                );
                handleShow(record.manual_email_trigger_uid);
              }}
            >
              View Delivery Report
            </Button>
            <Modal
              style={{
                fontFamily: "Lato",
                ...(deliveryReportData?.length > 10 && {
                  overflowY: "scroll",
                  maxHeight: "500px",
                }),
              }}
              width={"80%"}
              height={"80%"}
              open={showModal === record.manual_email_trigger_uid}
              onOk={handleOk}
              onCancel={handleClose}
              key={record.manual_email_trigger_uid}
            >
              {deliveryReportData && deliveryReportData.length > 0 ? (
                <EmailDeliveryReport
                  deliveryReportData={deliveryReportData}
                  slot_type="trigger"
                  email_config_uid={record.email_config_uid}
                />
              ) : (
                <p>
                  No Emails sent yet, delivery reports will be visible after
                  trigger time.
                </p>
              )}
            </Modal>
          </>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditTrigger(record)}
            >
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this trigger?"
            onConfirm={() => handleDeleteTrigger(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="link" icon={<DeleteOutlined />} danger>
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      {data.length > 0 ? (
        <ManualTriggersTable
          dataSource={data}
          columns={manualTriggerColumns}
          pagination={
            data.length > 10
              ? {
                  position: ["topRight"],
                  pageSize: paginationPageSize,
                  pageSizeOptions: [10, 25, 50, 100],
                  showSizeChanger: true,
                  showQuickJumper: true,
                  onShowSizeChange: (_, size) => setPaginationPageSize(size),
                }
              : false
          }
          rowKey={(record: any) => record.manual_email_trigger_uid}
          bordered={true}
        />
      ) : (
        <div
          style={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div>
            <img src={NotebooksImg} height={220} width={225} alt="Empty data" />
            <p
              style={{
                color: "#8C8C8C",
                fontFamily: "Lato",
                fontSize: "14px",
                lineHeight: "22px",
              }}
            >
              For this survey, email manual triggers have not yet been set up.
            </p>
          </div>
        </div>
      )}

      <Drawer
        title={"Edit Manual Trigger"}
        width={650}
        onClose={closeEditManualDrawer}
        open={isEditManualDrawerVisible}
        style={{ paddingBottom: 80, fontFamily: "Lato" }}
      >
        <ManualEmailTriggerForm
          closeAddManualDrawer={closeEditManualDrawer}
          surveyEnumerators={surveyEnumerators}
          initialValues={editTriggerValues}
          fetchManualTriggers={fetchManualTriggers}
          emailConfigData={emailConfigData}
          isEditMode={true}
        />
      </Drawer>
    </>
  );
}

export default ManualTriggers;
